import React, { useCallback, useEffect, useRef, useState } from 'react';
import { normalizeRotation, rotateBy, rotationLabel } from '../videoUpload/rotation';
import './VideoControlBar.css';

// The app's ONE set of playback controls. Every in-app player wears this bar —
// see VideoStage.js, which is the only thing that mounts it.
//
// It used to be the rotated-preview special case (a CSS transform turns the
// user-agent bar along with the picture, so a rotated player had to drop the
// native controls). That left the product with two different-looking players
// depending on whether a project happened to use rotation. The bar is now
// unconditional, so there is one player in the product and one place to fix.
//
// Driven entirely off the element's own events rather than a copy of its state,
// so it stays correct when something else moves the playhead — MotionTimeline
// seeks the same element by writing `currentTime` directly, and the editor's
// scrubber and this bar have to agree.

// How long the bar stays up after the mouse stops moving, while playing. Long
// enough to aim at a control you have just seen, short enough that the picture
// is clear for the "is this the right way up?" check the user is here to make.
const HIDE_AFTER_MS = 2500;

// How long a failed rotation's message stays up before it clears itself.
//
// `visible` below counts an error as a reason to hold the bar open, which is
// right for the moment the save is rejected and wrong a minute later: without
// an expiry, one refused write (offline, a permissions change) pinned the
// overlay and its red banner over the picture for the rest of the session — on
// the exact surface the user came here to LOOK at. Long enough to read one
// sentence, short enough to stop being chrome.
const ROTATE_ERROR_MS = 6000;

const SEEK_STEP_SECONDS = 5;

// Local, and not `photoUpload/caps: formatDuration`, which renders "about 2
// min" for upload estimates — wrong for a playhead. Not `FootageVideoList`'s
// `formatClock` either: that answers "—" for 0, which is right for a clip whose
// duration is unknown and wrong for a playback position that legitimately sits
// at zero.
const formatClock = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// Does this clip carry an audio track at all? Most Tweedia footage is silent
// timelapse, and a dead volume control on a silent clip is a control that
// teaches the user their sound is broken.
//
// There is no portable answer, so this asks the three engines in order of how
// much they actually know:
//   - `audioTracks` (Safari, and Firefox behind a flag) is definitive.
//   - `mozHasAudio` is definitive on Firefox.
//   - Chrome exposes only `webkitAudioDecodedByteCount`, which counts bytes
//     DECODED, so it is 0 until playback has actually fed the decoder. Reading
//     0 therefore means "nothing decoded yet", not "no audio" — which is why
//     this is re-run on every playback event rather than latched at load. A
//     clip that does have sound reveals its control a moment after it starts.
// Anything else — an engine that offers none of the three — is inconclusive,
// and inconclusive shows the control.
const detectAudio = (video) => {
  if (!video) return true;
  const tracks = video.audioTracks;
  if (tracks && typeof tracks.length === 'number') return tracks.length > 0;
  if (typeof video.mozHasAudio === 'boolean') return video.mozHasAudio;
  if (typeof video.webkitAudioDecodedByteCount === 'number') {
    return video.webkitAudioDecodedByteCount > 0;
  }
  return true;
};

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M8.2 4.8a1 1 0 0 1 1.53-.85l9.1 5.7a1.6 1.6 0 0 1 0 2.7l-9.1 5.7a1 1 0 0 1-1.53-.85z" />
  </svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
    <rect x="6.6" y="4.6" width="3.9" height="14.8" rx="1.3" />
    <rect x="13.5" y="4.6" width="3.9" height="14.8" rx="1.3" />
  </svg>
);

const VolumeIcon = () => (
  <svg
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"
  >
    <path d="M4 9.4h3.1L11.6 5.6v12.8L7.1 14.6H4z" fill="currentColor" stroke="none" />
    <path d="M15.4 9.3a3.8 3.8 0 0 1 0 5.4" />
    <path d="M18.1 6.6a7.6 7.6 0 0 1 0 10.8" />
  </svg>
);

const MutedIcon = () => (
  <svg
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"
  >
    <path d="M4 9.4h3.1L11.6 5.6v12.8L7.1 14.6H4z" fill="currentColor" stroke="none" />
    <line x1="15.4" y1="9.6" x2="20.4" y2="14.6" />
    <line x1="20.4" y1="9.6" x2="15.4" y2="14.6" />
  </svg>
);

const EnterFullscreenIcon = () => (
  <svg
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"
  >
    <path d="M4 9.2V5.8A1.8 1.8 0 0 1 5.8 4h3.4" />
    <path d="M14.8 4h3.4A1.8 1.8 0 0 1 20 5.8v3.4" />
    <path d="M20 14.8v3.4a1.8 1.8 0 0 1-1.8 1.8h-3.4" />
    <path d="M9.2 20H5.8A1.8 1.8 0 0 1 4 18.2v-3.4" />
  </svg>
);

const ExitFullscreenIcon = () => (
  <svg
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"
  >
    <path d="M9.4 4v3.6A1.8 1.8 0 0 1 7.6 9.4H4" />
    <path d="M20 9.4h-3.6a1.8 1.8 0 0 1-1.8-1.8V4" />
    <path d="M14.6 20v-3.6a1.8 1.8 0 0 1 1.8-1.8H20" />
    <path d="M4 14.6h3.6a1.8 1.8 0 0 1 1.8 1.8V20" />
  </svg>
);

const RotateLeftIcon = () => (
  <svg
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"
  >
    <polyline points="3 5 3 10.4 8.4 10.4" />
    <path d="M4.7 15.1a8.4 8.4 0 1 0 2-8.7L3 10.4" />
  </svg>
);

const RotateRightIcon = () => (
  <svg
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"
  >
    <polyline points="21 5 21 10.4 15.6 10.4" />
    <path d="M19.3 15.1a8.4 8.4 0 1 1-2-8.7L21 10.4" />
  </svg>
);

const VideoControlBar = ({
  // The <video> element this bar drives. Null until the player mounts, and null
  // for good in suites that stub the player out — every read below is guarded.
  video,
  // The player's outer box: the element that holds BOTH the (possibly rotated)
  // picture and this bar. Fullscreen is requested on this, never on the
  // <video>, or the browser would present the raw element — losing the CSS
  // rotation and this bar with it. Also the surface hover and the keyboard
  // shortcuts are scoped to, so two players on one page can't fight.
  container = null,
  // Optional rotate segment. Absent `onRotate` renders nothing at all: the
  // rendered-output and read-only players must never offer it.
  rotation = 0,
  // (nextRotation) => Promise. Rejects with a user-facing Error message.
  onRotate = null,
  rotateDisabled = false,
}) => {
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [hasAudio, setHasAudio] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  // Recent mouse activity over the player. The only input to auto-hide besides
  // "is it playing" — focus is handled by the bar's own onFocus/onBlur below.
  const [pointerActive, setPointerActive] = useState(false);
  const [focused, setFocused] = useState(false);
  const [rotatePending, setRotatePending] = useState(false);
  const [rotateError, setRotateError] = useState(null);
  const idleTimer = useRef(null);

  useEffect(() => {
    if (!video) return undefined;
    const sync = () => {
      setPlaying(!video.paused && !video.ended);
      setElapsed(video.currentTime || 0);
      setDuration(Number.isFinite(video.duration) ? video.duration : 0);
      setMuted(!!video.muted);
      setVolume(Number.isFinite(video.volume) ? video.volume : 1);
      setHasAudio(detectAudio(video));
    };
    // Read once on mount as well as on every event: the element may already be
    // loaded and playing by the time this bar attaches (React commits the video
    // before the effect runs), and without this the bar would open showing a
    // play button over a video that is already running.
    sync();
    const events = [
      'play', 'pause', 'ended', 'timeupdate', 'durationchange', 'loadedmetadata',
      'loadeddata', 'volumechange', 'emptied', 'playing',
    ];
    events.forEach((name) => video.addEventListener(name, sync));
    return () => events.forEach((name) => video.removeEventListener(name, sync));
  }, [video]);

  const bump = useCallback(() => {
    setPointerActive(true);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setPointerActive(false), HIDE_AFTER_MS);
  }, []);

  // Activity over the player, scoped to the player rather than the document: a
  // page can show more than one of these (Overview stacks the source player
  // above the rendered result), and a document-level listener would light both.
  //
  // POINTER events, not mouse: they cover mouse, pen and touch through one set
  // of handlers. On the mouse-only version a touch user got the bar back purely
  // by way of the browser's synthesised mouse events, which meant the first tap
  // after the 2.5s auto-hide was spent re-showing the bar rather than pressing
  // anything on it.
  useEffect(() => {
    if (!container) return undefined;
    const leave = (event) => {
      // A touch pointer stops existing the moment the finger lifts, and the
      // browser fires `pointerleave` right then — so treating every leave as
      // "no longer looking at this" would hide the bar at the end of the very
      // tap that summoned it. Only a mouse (or a pen) leaving the player is a
      // real departure; touch falls through to the idle timer instead.
      if (event.pointerType && event.pointerType === 'touch') return;
      if (idleTimer.current) clearTimeout(idleTimer.current);
      setPointerActive(false);
    };
    container.addEventListener('pointermove', bump);
    container.addEventListener('pointerenter', bump);
    container.addEventListener('pointerleave', leave);
    return () => {
      container.removeEventListener('pointermove', bump);
      container.removeEventListener('pointerenter', bump);
      container.removeEventListener('pointerleave', leave);
    };
  }, [container, bump]);

  useEffect(() => () => { if (idleTimer.current) clearTimeout(idleTimer.current); }, []);

  // Retire the rotation error on a clock, so a failed save is reported and then
  // gets out of the way. Keyed on the message itself: a second failure re-arms
  // the countdown rather than inheriting the first one's remaining time.
  useEffect(() => {
    if (!rotateError) return undefined;
    const timer = setTimeout(() => setRotateError(null), ROTATE_ERROR_MS);
    return () => clearTimeout(timer);
  }, [rotateError]);

  const togglePlay = useCallback(() => {
    if (!video) return;
    // `play()` rejects when the element is torn down mid-gesture, or when a
    // codec the browser cannot decode fails after the click. Unhandled it
    // surfaces as a console error on a control the user has already moved on
    // from; the pause/play state resyncs from the element's own events anyway.
    if (video.paused || video.ended) {
      const started = video.play();
      if (started && typeof started.catch === 'function') started.catch(() => {});
    } else {
      video.pause();
    }
  }, [video]);

  const seekTo = useCallback((seconds) => {
    if (!video) return;
    const limit = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
    const next = Math.max(0, limit === null ? seconds : Math.min(seconds, limit));
    video.currentTime = next;
  }, [video]);

  const nudge = useCallback((deltaSeconds) => {
    if (!video) return;
    seekTo((video.currentTime || 0) + deltaSeconds);
  }, [video, seekTo]);

  const toggleMute = useCallback(() => {
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  }, [video]);

  // Presented on the CONTAINER. Handing the <video> to the fullscreen API would
  // hand over the untransformed element, so a 90° project would go fullscreen
  // on its side and this bar would vanish with it.
  const toggleFullscreen = useCallback(() => {
    if (!container) return;
    const doc = container.ownerDocument || document;
    const active = doc.fullscreenElement || doc.webkitFullscreenElement;
    if (active) {
      const exit = doc.exitFullscreen || doc.webkitExitFullscreen;
      if (typeof exit === 'function') {
        const left = exit.call(doc);
        if (left && typeof left.catch === 'function') left.catch(() => {});
      }
      return;
    }
    const request = container.requestFullscreen || container.webkitRequestFullscreen;
    // Absent on iPhone Safari, where only the <video> may go fullscreen — and
    // that is precisely the presentation this bar exists to avoid, so the
    // control simply does nothing there rather than showing a sideways player.
    if (typeof request !== 'function') return;
    const entered = request.call(container);
    if (entered && typeof entered.catch === 'function') entered.catch(() => {});
  }, [container]);

  // The class, not the `:fullscreen` pseudo-class, because the three prefixed
  // spellings of that selector cannot share one rule (an engine that does not
  // understand one of them drops the whole block). One class keeps the
  // fullscreen layout rules in a single place — see VideoControlBar.css.
  useEffect(() => {
    if (!container) return undefined;
    const doc = container.ownerDocument || document;
    const sync = () => {
      const active = doc.fullscreenElement || doc.webkitFullscreenElement;
      const isFs = active === container;
      setFullscreen(isFs);
      container.classList.toggle('tw-stage--fs', isFs);
    };
    sync();
    doc.addEventListener('fullscreenchange', sync);
    doc.addEventListener('webkitfullscreenchange', sync);
    return () => {
      doc.removeEventListener('fullscreenchange', sync);
      doc.removeEventListener('webkitfullscreenchange', sync);
      container.classList.remove('tw-stage--fs');
    };
  }, [container]);

  // Scoped to the player, not the document: these keys belong to whichever
  // player the user is actually pointed at, and Space on a page-level listener
  // would hijack scrolling everywhere else in the studio.
  useEffect(() => {
    if (!container) return undefined;
    const onKeyDown = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const tag = event.target?.tagName;
      const onRange = tag === 'INPUT';
      const onButton = tag === 'BUTTON';
      switch (event.key) {
        case ' ':
        case 'k':
        case 'K':
          // Space on a focused button already activates it; a second handler
          // would toggle playback twice per press.
          if (onButton) return;
          event.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          // The seek and volume inputs answer the arrows natively, and their
          // onChange already writes through to the element.
          if (onRange) return;
          event.preventDefault();
          nudge(-SEEK_STEP_SECONDS);
          break;
        case 'ArrowRight':
          if (onRange) return;
          event.preventDefault();
          nudge(SEEK_STEP_SECONDS);
          break;
        case 'm':
        case 'M':
          event.preventDefault();
          toggleMute();
          break;
        case 'f':
        case 'F':
          event.preventDefault();
          toggleFullscreen();
          break;
        default:
          break;
      }
      bump();
    };
    container.addEventListener('keydown', onKeyDown);
    return () => container.removeEventListener('keydown', onKeyDown);
  }, [container, togglePlay, nudge, toggleMute, toggleFullscreen, bump]);

  const currentRotation = normalizeRotation(rotation);
  const rotateLocked = rotateDisabled || rotatePending || !onRotate;

  const applyRotation = async (next) => {
    if (rotateLocked || next === currentRotation) return;
    setRotateError(null);
    setRotatePending(true);
    try {
      await onRotate(next);
    } catch (err) {
      setRotateError(err?.message || 'Could not save the rotation.');
    } finally {
      setRotatePending(false);
    }
  };

  // A zero max would pin the thumb and make the bar look broken during the
  // moment before metadata lands, so hold it at 1 until a real duration
  // arrives.
  const span = duration > 0 ? duration : 1;
  const position = Math.min(Math.max(elapsed, 0), span);
  const progressPct = `${(position / span) * 100}%`;

  // Held up while a rotation is saving or has just failed: the user has pressed
  // a control and is owed the outcome, even mid-playback. The error half of
  // that expires on its own (ROTATE_ERROR_MS), so the hold is temporary.
  const visible = !playing || pointerActive || focused || rotatePending || !!rotateError;

  // Clicking a control focuses it too, and pinning the bar for that would mean
  // it never auto-hides again for anyone who pressed Play with a mouse — which
  // is most people. `:focus-visible` is exactly the "arrived here by keyboard"
  // question, so it is the one asked. Guarded because a browser without the
  // selector throws on `matches`, and the safe answer there is to keep the bar.
  const noteFocus = (event) => {
    let keyboard = true;
    try {
      keyboard = event.target.matches(':focus-visible');
    } catch (err) {
      keyboard = true;
    }
    setFocused(keyboard);
  };

  return (
    <div
      className="tw-vcb"
      data-testid="video-control-bar"
      data-visible={visible ? 'true' : 'false'}
      // Focus is tracked on the BAR, not on the player box: the box takes focus
      // from an ordinary click on the picture, and pinning the bar open for
      // that would defeat auto-hide entirely.
      onFocus={noteFocus}
      onBlur={() => setFocused(false)}
    >
      {rotateError && (
        <p className="tw-vcb-error" role="alert" data-testid="video-control-rotate-error">
          {rotateError}
        </p>
      )}

      <button
        type="button"
        className="tw-vcb-btn tw-vcb-play"
        onClick={togglePlay}
        aria-label={playing ? 'Pause' : 'Play'}
        data-testid="video-control-play"
      >
        {playing ? <PauseIcon /> : <PlayIcon />}
      </button>

      {/* The separator sits INSIDE the total, so a narrow player can drop the
          clip length (see the container query in the stylesheet) and be left
          with "0:03" rather than a dangling "0:03 /". */}
      <span className="tw-vcb-time" data-testid="video-control-time">
        <b>{formatClock(elapsed)}</b>
        <span className="tw-vcb-total"> / {formatClock(duration)}</span>
      </span>

      <input
        type="range"
        className="tw-vcb-seek"
        min="0"
        max={span}
        step="any"
        value={position}
        onChange={(event) => seekTo(Number(event.target.value))}
        style={{ '--tw-vcb-progress': progressPct }}
        aria-label="Seek"
        data-testid="video-control-seek"
      />

      {onRotate && (
        <>
          {/* A hairline, so the rotate cluster reads as a separate instrument
              rather than another transport button. Turning the footage is not
              playback. */}
          <span className="tw-vcb-divider" aria-hidden="true" />
          <span className="tw-vcb-cluster" data-testid="video-control-rotate">
            <button
              type="button"
              className="tw-vcb-btn"
              aria-label="Rotate footage 90 degrees left"
              title="Rotate left"
              disabled={rotateLocked}
              onClick={() => applyRotation(rotateBy(currentRotation, -1))}
              data-testid="video-control-rotate-left"
            >
              <RotateLeftIcon />
            </button>
            <span
              className="tw-vcb-rotate-state"
              data-testid="video-control-rotate-state"
              title={rotationLabel(currentRotation) || 'Not rotated'}
            >
              {currentRotation}&deg;
            </span>
            <button
              type="button"
              className="tw-vcb-btn"
              aria-label="Rotate footage 90 degrees right"
              title="Rotate right"
              disabled={rotateLocked}
              onClick={() => applyRotation(rotateBy(currentRotation, 1))}
              data-testid="video-control-rotate-right"
            >
              <RotateRightIcon />
            </button>
          </span>
        </>
      )}

      <span className="tw-vcb-divider" aria-hidden="true" />

      {hasAudio && (
        <span className="tw-vcb-volume" data-testid="video-control-volume">
          <button
            type="button"
            className="tw-vcb-btn"
            onClick={toggleMute}
            aria-label={muted ? 'Unmute' : 'Mute'}
            data-testid="video-control-mute"
          >
            {muted ? <MutedIcon /> : <VolumeIcon />}
          </button>
          <input
            type="range"
            className="tw-vcb-volume-slider"
            min="0"
            max="1"
            step="0.05"
            value={muted ? 0 : volume}
            onChange={(event) => {
              if (!video) return;
              const next = Number(event.target.value);
              video.volume = next;
              // Dragging away from silence is an unmute — otherwise the slider
              // moves and nothing is heard.
              video.muted = next === 0;
              setVolume(next);
              setMuted(next === 0);
            }}
            style={{ '--tw-vcb-progress': `${(muted ? 0 : volume) * 100}%` }}
            aria-label="Volume"
            data-testid="video-control-volume-slider"
          />
        </span>
      )}

      <button
        type="button"
        className="tw-vcb-btn"
        onClick={toggleFullscreen}
        aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
        data-testid="video-control-fullscreen"
      >
        {fullscreen ? <ExitFullscreenIcon /> : <EnterFullscreenIcon />}
      </button>
    </div>
  );
};

export default VideoControlBar;
